import type { Bidder } from '../data/mockLots';
import { revealProps } from '../lib/motion';

// Round, hue-tinted avatar matching the app's BidderAvatar / LotDetail leader
// slot exactly (same gradient + glow ring), just larger and photo-aware. The
// hue comes from placeholderBidder, so a user reads identically here and in a
// lot's bid history.
function Avatar({
  bidder,
  photoUrl,
  size = 60,
}: {
  bidder: Bidder;
  photoUrl: string | null;
  size?: number;
}) {
  const ring = `0 0 0 1px hsl(${bidder.hue}, 60%, 30%), 0 0 ${size * 0.35}px hsla(${bidder.hue}, 80%, 50%, 0.35)`;
  if (photoUrl) {
    return (
      <img
        src={photoUrl}
        alt=""
        width={size}
        height={size}
        className="rounded-full object-cover shrink-0"
        style={{ boxShadow: ring }}
      />
    );
  }
  return (
    <div
      className="inline-flex items-center justify-center rounded-full font-mono font-semibold text-bg shrink-0"
      style={{
        width: size,
        height: size,
        background: `linear-gradient(135deg, hsl(${bidder.hue}, 90%, 65%) 0%, hsl(${(bidder.hue + 40) % 360}, 85%, 50%) 100%)`,
        fontSize: size * 0.42,
        boxShadow: ring,
      }}
    >
      {bidder.initial}
    </div>
  );
}

function HeaderStat({ label, value }: { label: string; value: number }) {
  return (
    <div className="text-center">
      <span className="block font-mono text-[8.5px] tracking-[0.18em] text-fg-muted uppercase">
        {label}
      </span>
      <span
        className={`block font-mono font-bold text-[22px] tabular-nums leading-none mt-1.5 ${value > 0 ? 'text-primary' : 'text-fg-dim'}`}
        style={value > 0 ? { textShadow: '0 0 12px rgba(0,229,255,0.4)' } : undefined}
      >
        {value}
      </span>
    </div>
  );
}

export function ProfileHeader({
  bidder,
  photoUrl,
  wins,
  refBids,
  index = 0,
}: {
  bidder: Bidder;
  photoUrl: string | null;
  wins: number;
  refBids: number;
  index?: number;
}) {
  return (
    <section
      {...revealProps(index, {
        className:
          'relative mx-3 mt-4 hud-corners overflow-hidden border border-edge bg-bg-elev/40',
      })}
    >
      <div className="absolute inset-0 pointer-events-none scanlines opacity-30" />

      <div className="relative flex items-center gap-3.5 px-4 py-4">
        <Avatar bidder={bidder} photoUrl={photoUrl} />
        <div className="min-w-0 flex-1">
          <h2 className="font-display font-bold text-[20px] leading-none tracking-tight text-fg truncate">
            {bidder.firstName}
          </h2>
          {bidder.username && (
            <p className="font-mono text-[11px] tracking-wider text-fg-muted mt-1.5 truncate">
              @{bidder.username}
            </p>
          )}
        </div>

        <div className="flex items-start gap-7 shrink-0">
          <HeaderStat label="Lots won" value={wins} />
          <HeaderStat label="Ref bids" value={refBids} />
        </div>
      </div>
    </section>
  );
}
